import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { AfRouterAdapter, CetusAggregatorAdapter, Router } from '@kunalabs-io/kai'
import { AggregatorClient as CetusAggregatorClient } from '@cetusprotocol/aggregator-sdk'

export enum SwapRouter {
  Cetus = 'Cetus',
  Aftermath = 'Aftermath',
}

interface SwapContext {
  router: Router
  routerLabel: SwapRouter
  onRouterChange: (router: string) => void
}

export const SWAP_ROUTER_KEY = 'swap-router'
export const ROUTER_OPTIONS = [SwapRouter.Cetus, SwapRouter.Aftermath]

const getInitialRouterLabel = () => {
  const storedRouter = localStorage.getItem(SWAP_ROUTER_KEY)
  if (storedRouter === SwapRouter.Cetus) {
    return SwapRouter.Cetus
  }
  return SwapRouter.Aftermath
}

export const SwapRouterProvider = ({ children }: { children: ReactNode }) => {
  const [activeRouterLabel, setActiveRouterLabel] = useState(getInitialRouterLabel)
  // Cetus is constructed synchronously; the Aftermath router resolves its
  // on-chain addresses over the network, so it is created via the async
  // factory once on mount (its constructor is private).
  const [cetusRouter] = useState(() => new CetusAggregatorAdapter(new CetusAggregatorClient({})))
  const [afRouter, setAfRouter] = useState<AfRouterAdapter | null>(null)

  useEffect(() => {
    let cancelled = false
    void AfRouterAdapter.create().then(router => {
      if (!cancelled) setAfRouter(router)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // While the Aftermath router is still initializing, fall back to the
  // always-ready Cetus router so consumers always have a usable router.
  const activeRouter: Router = activeRouterLabel === SwapRouter.Aftermath ? (afRouter ?? cetusRouter) : cetusRouter

  const handleRouterChange = useCallback((router: string) => {
    switch (router) {
      case SwapRouter.Cetus:
        setActiveRouterLabel(SwapRouter.Cetus)
        localStorage.setItem(SWAP_ROUTER_KEY, SwapRouter.Cetus)
        break
      case SwapRouter.Aftermath:
        setActiveRouterLabel(SwapRouter.Aftermath)
        localStorage.setItem(SWAP_ROUTER_KEY, SwapRouter.Aftermath)
        break
      default:
        return
    }
  }, [])

  return (
    <SwapRouterContext.Provider
      value={{ routerLabel: activeRouterLabel, router: activeRouter, onRouterChange: handleRouterChange }}
    >
      {children}
    </SwapRouterContext.Provider>
  )
}
const SwapRouterContext = createContext<SwapContext | null>(null)

export const useRouter = (): SwapContext => {
  const router = useContext(SwapRouterContext)
  if (!router) {
    throw new Error('useRouter must be used within a RouterProvider')
  }
  return router
}
